import { styled } from "@pigment-css/react";
import { useTranslations } from "@acto/web-common/locales";
import { SidePanelSection } from "../../ui";
import { Repeat } from "@acto/web-common/components";

interface WorkflowConfigSectionProps {
  repeat: boolean;
  dueDate?: string | null;
  pauseTill?: string | null;
  responsibleEmployeeName?: string | null;
}

const ConfigList = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  gap: 0,
}));

const ConfigItem = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  height: "32px",
  padding: `${theme.managementView.spacing[4]} 0`,
  borderBottom: `1px solid ${theme.managementView.colors.neutral[4]}`,
  "&:last-child": {
    borderBottom: "none",
  },
}));

const SectionTitle = styled("h3")(({ theme }) => ({
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: theme.managementView.font.size[15],
  fontWeight: theme.managementView.font.weight[440],
  letterSpacing: theme.managementView.font.letterSpacing.tight,
  color: theme.managementView.colors.neutral[56],
  margin: 0,
}));

const ConfigLabel = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.managementView.spacing[6],
  width: "200px",
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: theme.managementView.font.size[14],
  fontWeight: theme.managementView.font.weight[400],
  color: theme.managementView.colors.neutral[90],
}));

const ConfigValue = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: theme.managementView.font.size[14],
  fontWeight: theme.managementView.font.weight[400],
  color: theme.managementView.colors.neutral[90],
  padding: `${theme.managementView.spacing[6]}`,
}));

const isSameDay = (value?: string | null): boolean => !!value && /^0[dwmy]$/.test(value);

export const WorkflowConfigSection = ({
  repeat,
  dueDate,
  pauseTill,
  responsibleEmployeeName,
}: WorkflowConfigSectionProps) => {
  const t = useTranslations("managementView.workflowDetails");

  return (
    <SidePanelSection>
      <SectionTitle>{t("configuration")}</SectionTitle>
      <ConfigList>
        <ConfigItem>
          <ConfigLabel>
            {t("frequency")}
            <Repeat style={{ width: "14px", height: "14px", flexShrink: 0 }} />
          </ConfigLabel>
          <ConfigValue>{repeat ? t("repeating") : t("oneTime")}</ConfigValue>
        </ConfigItem>
        {repeat && pauseTill && (
          <ConfigItem>
            <ConfigLabel>{t("pauseTill")}</ConfigLabel>
            <ConfigValue>{pauseTill}</ConfigValue>
          </ConfigItem>
        )}
        <ConfigItem>
          <ConfigLabel>{t("dueDate")}</ConfigLabel>
          <ConfigValue>{isSameDay(dueDate) ? t("sameDay") : dueDate}</ConfigValue>
        </ConfigItem>
        {responsibleEmployeeName && (
          <ConfigItem>
            <ConfigLabel>{t("responsiblePerson")}</ConfigLabel>
            <ConfigValue>{responsibleEmployeeName}</ConfigValue>
          </ConfigItem>
        )}
      </ConfigList>
    </SidePanelSection>
  );
};
